import { useState, Fragment } from "react";
import { Table, TableWrapper, SortableHeader } from "../../components/ui";
import { HexDisplay } from "../../components/HexDisplay";
import type {
  L2ToL1Message,
  PrivateLogDetail,
  PublicLogDetail,
  ContractClassLogDetail,
} from "../../lib/api";
import { useSortableTable } from "./utils";
import { Collapsible } from "./Collapsible";
import { CalldataCell, CalldataToggle, TableCard, ScrollableFields, SmallMono } from "./styled";

function LogFieldsList({ fields }: { fields: string[] }) {
  return (
    <ScrollableFields>
      {fields.map((field, j) => (
        <div
          key={j}
          style={{
            display: "flex",
            gap: "8px",
            alignItems: "baseline",
          }}
        >
          <span
            style={{
              color: "#8888aa",
              fontSize: "10px",
              minWidth: "20px",
              textAlign: "right",
            }}
          >
            {j}
          </span>
          <SmallMono style={{ wordBreak: "break-all" }}>
            <HexDisplay address={field} abbreviate={false} mode="hex" link={false} />
          </SmallMono>
        </div>
      ))}
    </ScrollableFields>
  );
}

export function L2ToL1MessagesSection({ messages }: { messages: L2ToL1Message[] }) {
  const { sorted, toggleSort, indicator, sortKey } = useSortableTable(
    messages,
    "position",
  );
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const toggle = (i: number) =>
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  if (messages.length === 0) return null;

  return (
    <Collapsible title="L2→L1 Messages" count={messages.length} defaultOpen={true}>
      <TableCard>
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <SortableHeader
                  $active={sortKey === "position"}
                  onClick={() => toggleSort("position")}
                >
                  #{indicator("position")}
                </SortableHeader>
                <SortableHeader
                  $active={sortKey === "recipient"}
                  onClick={() => toggleSort("recipient")}
                >
                  Recipient (L1){indicator("recipient")}
                </SortableHeader>
                <SortableHeader
                  $active={sortKey === "sender"}
                  onClick={() => toggleSort("sender")}
                >
                  Sender (L2){indicator("sender")}
                </SortableHeader>
                <th>Content</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((msg) => {
                const isExpanded = expandedRows.has(msg.position);
                const hasContent = !!msg.content;
                if (msg.fromPublicExecution) {
                  return (
                    <Fragment key={msg.position}>
                      <tr>
                        <td>{msg.position}</td>
                        <td colSpan={2}>
                          <span style={{ color: "#8888aa", fontSize: "11px", fontStyle: "italic" }}>
                            emitted by public execution — details unavailable
                          </span>
                        </td>
                        <td>
                          {msg.messageHash ? (
                            <CalldataToggle onClick={() => toggle(msg.position)}>
                              hash {isExpanded ? "▾" : "▸"}
                            </CalldataToggle>
                          ) : "\u2014"}
                        </td>
                      </tr>
                      {isExpanded && msg.messageHash && (
                        <tr>
                          <CalldataCell colSpan={4}>
                            <span style={{ color: "#8888aa", fontSize: "11px" }}>Hash: </span>
                            <SmallMono style={{ wordBreak: "break-all" }}>
                              <HexDisplay address={msg.messageHash} abbreviate={false} mode="hex" link={false} />
                            </SmallMono>
                          </CalldataCell>
                        </tr>
                      )}
                    </Fragment>
                  );
                }
                return (
                  <Fragment key={msg.position}>
                    <tr>
                      <td>{msg.position}</td>
                      <td>
                        {msg.recipient ? (
                          <HexDisplay address={msg.recipient} />
                        ) : (
                          "\u2014"
                        )}
                      </td>
                      <td>
                        {msg.sender ? (
                          <HexDisplay address={msg.sender} />
                        ) : (
                          "\u2014"
                        )}
                      </td>
                      <td>
                        {hasContent ? (
                          <CalldataToggle onClick={() => toggle(msg.position)}>
                            {isExpanded ? "hide ▾" : "show ▸"}
                          </CalldataToggle>
                        ) : (
                          "\u2014"
                        )}
                      </td>
                    </tr>
                    {isExpanded && msg.content && (
                      <tr>
                        <CalldataCell colSpan={4}>
                          <SmallMono style={{ wordBreak: "break-all" }}>
                            <HexDisplay address={msg.content} abbreviate={false} mode="hex" link={false} />
                          </SmallMono>
                          {msg.messageHash && (
                            <div style={{ marginTop: 6 }}>
                              <span style={{ color: "#8888aa", fontSize: "11px" }}>Hash: </span>
                              <SmallMono style={{ wordBreak: "break-all" }}>
                                <HexDisplay address={msg.messageHash} abbreviate={false} mode="hex" link={false} />
                              </SmallMono>
                            </div>
                          )}
                        </CalldataCell>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </Table>
        </TableWrapper>
      </TableCard>
    </Collapsible>
  );
}

export function PrivateLogsSection({ logs }: { logs: PrivateLogDetail[] }) {
  const { sorted, toggleSort, indicator, sortKey } = useSortableTable(
    logs,
    "index",
  );
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const toggle = (i: number) =>
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  if (logs.length === 0) return null;

  return (
    <Collapsible title="Private Logs" count={logs.length} defaultOpen={false}>
      <TableCard>
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <SortableHeader
                  $active={sortKey === "index"}
                  onClick={() => toggleSort("index")}
                >
                  #{indicator("index")}
                </SortableHeader>
                <SortableHeader
                  $active={sortKey === "emittedLength"}
                  onClick={() => toggleSort("emittedLength")}
                >
                  Emitted Length{indicator("emittedLength")}
                </SortableHeader>
              </tr>
            </thead>
            <tbody>
              {sorted.map((log) => {
                const isExpanded = expandedRows.has(log.index);
                const hasFields = log.fields.length > 0;
                return (
                  <Fragment key={log.index}>
                    <tr>
                      <td>{log.index}</td>
                      <td>
                        {hasFields ? (
                          <CalldataToggle onClick={() => toggle(log.index)}>
                            {log.emittedLength} fields {isExpanded ? "▾" : "▸"}
                          </CalldataToggle>
                        ) : (
                          log.emittedLength.toLocaleString()
                        )}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <CalldataCell colSpan={2}>
                          <LogFieldsList fields={log.fields} />
                        </CalldataCell>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </Table>
        </TableWrapper>
      </TableCard>
    </Collapsible>
  );
}

export function PublicLogsSection({ logs }: { logs: PublicLogDetail[] }) {
  const { sorted, toggleSort, indicator, sortKey } = useSortableTable(
    logs,
    "index",
  );
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const toggle = (i: number) =>
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  if (logs.length === 0) return null;

  return (
    <Collapsible title="Public Logs" count={logs.length} defaultOpen={false}>
      <TableCard>
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <SortableHeader
                  $active={sortKey === "index"}
                  onClick={() => toggleSort("index")}
                >
                  #{indicator("index")}
                </SortableHeader>
                <SortableHeader
                  $active={sortKey === "contractAddress"}
                  onClick={() => toggleSort("contractAddress")}
                >
                  Contract Address{indicator("contractAddress")}
                </SortableHeader>
                <SortableHeader
                  $active={sortKey === "emittedLength"}
                  onClick={() => toggleSort("emittedLength")}
                >
                  Fields{indicator("emittedLength")}
                </SortableHeader>
              </tr>
            </thead>
            <tbody>
              {sorted.map((log) => {
                const isExpanded = expandedRows.has(log.index);
                const hasFields = log.fields.length > 0;
                return (
                  <Fragment key={log.index}>
                    <tr>
                      <td>{log.index}</td>
                      <td>
                        {log.contractAddress ? (
                          <HexDisplay address={log.contractAddress} />
                        ) : (
                          "\u2014"
                        )}
                      </td>
                      <td>
                        {hasFields ? (
                          <CalldataToggle onClick={() => toggle(log.index)}>
                            {log.emittedLength} fields {isExpanded ? "▾" : "▸"}
                          </CalldataToggle>
                        ) : (
                          log.emittedLength.toLocaleString()
                        )}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <CalldataCell colSpan={3}>
                          <LogFieldsList fields={log.fields} />
                        </CalldataCell>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </Table>
        </TableWrapper>
      </TableCard>
    </Collapsible>
  );
}

export function ContractClassLogsSection({
  logs,
}: {
  logs: ContractClassLogDetail[];
}) {
  const { sorted, toggleSort, indicator, sortKey } = useSortableTable(
    logs,
    "index",
  );
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const toggle = (i: number) =>
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  if (logs.length === 0) return null;

  return (
    <Collapsible
      title="Contract Class Logs"
      count={logs.length}
      defaultOpen={false}
    >
      <TableCard>
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <SortableHeader
                  $active={sortKey === "index"}
                  onClick={() => toggleSort("index")}
                >
                  #{indicator("index")}
                </SortableHeader>
                <SortableHeader
                  $active={sortKey === "contractAddress"}
                  onClick={() => toggleSort("contractAddress")}
                >
                  Contract Address{indicator("contractAddress")}
                </SortableHeader>
                <SortableHeader
                  $active={sortKey === "contractClassId"}
                  onClick={() => toggleSort("contractClassId")}
                >
                  Contract Class ID{indicator("contractClassId")}
                </SortableHeader>
                <SortableHeader
                  $active={sortKey === "emittedLength"}
                  onClick={() => toggleSort("emittedLength")}
                >
                  Fields{indicator("emittedLength")}
                </SortableHeader>
              </tr>
            </thead>
            <tbody>
              {sorted.map((log) => {
                const isExpanded = expandedRows.has(log.index);
                const hasFields = log.fields.length > 0;
                return (
                  <Fragment key={log.index}>
                    <tr>
                      <td>{log.index}</td>
                      <td>
                        {log.contractAddress ? (
                          <HexDisplay address={log.contractAddress} />
                        ) : (
                          "\u2014"
                        )}
                      </td>
                      <td>
                        {log.contractClassId ? (
                          <HexDisplay address={log.contractClassId} link={false} />
                        ) : (
                          "\u2014"
                        )}
                      </td>
                      <td>
                        {hasFields ? (
                          <CalldataToggle onClick={() => toggle(log.index)}>
                            {log.emittedLength} fields {isExpanded ? "▾" : "▸"}
                          </CalldataToggle>
                        ) : (
                          log.emittedLength.toLocaleString()
                        )}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <CalldataCell colSpan={4}>
                          <LogFieldsList fields={log.fields} />
                        </CalldataCell>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </Table>
        </TableWrapper>
      </TableCard>
    </Collapsible>
  );
}
